/**
 * Permissions utility
 * Centralized permission checking for role-based access control
 */

import { UserRole } from '../types';

/**
 * Admin roles that have full system access
 */
export const ADMIN_ROLES: UserRole[] = ['super_admin', 'tenant_admin'];

/**
 * Manager roles that have elevated permissions
 */
export const MANAGER_ROLES: UserRole[] = ['super_admin', 'tenant_admin', 'manager'];

/**
 * Normalize role to string - handles both string roles and numeric role codes
 */
export const normalizeRole = (role: UserRole | number | string | null | undefined): UserRole | null => {
  if (!role && role !== 0) return null;
  if (typeof role === 'string') return role as UserRole;
  // Map numeric role codes to string roles
  const roleMap: Record<number, UserRole> = {
    1: 'super_admin',
    2: 'tenant_admin',
    3: 'manager',
    4: 'employee',
    5: 'external',
  };
  return roleMap[role] || null;
};

/**
 * Check if user is an admin (super_admin or tenant_admin)
 */
export const isAdmin = (role?: UserRole | number | null): boolean => {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return ADMIN_ROLES.includes(normalized);
};

/**
 * Check if user is a manager or above
 */
export const isManager = (role?: UserRole | number | null): boolean => {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return MANAGER_ROLES.includes(normalized);
};

type RoleInput = UserRole | number | null | undefined;

/**
 * Check if user is a super admin (platform-wide admin)
 */
export const isSuperAdmin = (role?: RoleInput): boolean => {
  return normalizeRole(role) === 'super_admin';
};

/**
 * Check if user is a tenant admin (organization admin)
 */
export const isTenantAdmin = (role?: RoleInput): boolean => {
  return normalizeRole(role) === 'tenant_admin';
};

/**
 * Check if user has permission to manage users
 */
export const canManageUsers = (role?: RoleInput): boolean => {
  return isAdmin(role);
};

/**
 * Check if user has permission to manage assets
 */
export const canManageAssets = (role?: RoleInput): boolean => {
  return isManager(role);
};

/**
 * Check if user has permission to create/edit forms
 */
export const canManageForms = (role?: RoleInput): boolean => {
  return isManager(role);
};

/**
 * Check if user has permission to approve submissions
 */
export const canApproveSubmissions = (role?: RoleInput): boolean => {
  return isManager(role);
};

/**
 * Check if user has permission to view analytics
 */
export const canViewAnalytics = (role?: RoleInput): boolean => {
  return isManager(role);
};

/**
 * Check if user has permission to manage billing
 */
export const canManageBilling = (role?: RoleInput): boolean => {
  return isAdmin(role);
};

/**
 * Check if user has permission to manage settings
 */
export const canManageSettings = (role?: RoleInput): boolean => {
  return isAdmin(role);
};

/**
 * Check if user has permission to manage asset types
 */
export const canManageAssetTypes = (role?: RoleInput): boolean => {
  return isAdmin(role);
};

/**
 * Check if user has permission to manage maintenance schedules
 */
export const canManageMaintenance = (role?: RoleInput): boolean => {
  return isManager(role);
};

/**
 * Check if user can submit forms (all authenticated users can)
 */
export const canSubmitForms = (role?: RoleInput): boolean => {
  return !!role; // All authenticated users
};

/**
 * Check if user can view submissions
 */
export const canViewSubmissions = (role?: RoleInput): boolean => {
  return !!role; // All authenticated users
};
